import { useState, useCallback } from 'react';
import type { Product, OnboardingSystem } from '../pages/CatalogPage';

export type OnboardingCountry = {
  country: string;
  countryName: string;
  countryFlag: string;
  systems: OnboardingSystem[];
};

type ModalState = {
  // Device Modal
  selectedDevice: Product | null;
  showDeviceModal: boolean;
  handleDeviceClick: (device: Product) => void;
  handleCloseDeviceModal: () => void;

  // Partner Modal
  selectedPartnerProduct: Product | null;
  showPartnerModal: boolean;
  handlePartnerClick: (product: Product) => void;
  handleClosePartnerModal: () => void;

  // Product Modal
  selectedProduct: Product | null;
  showProductModal: boolean;
  handleProductClick: (product: Product) => void;
  handleCloseProductModal: () => void;

  // Onboarding Modal
  selectedOnboardingCountry: OnboardingCountry | null;
  showOnboardingModal: boolean;
  handleOnboardingClick: (data: OnboardingCountry) => void;
  handleCloseOnboardingModal: () => void;
  setSelectedOnboardingCountry: React.Dispatch<React.SetStateAction<OnboardingCountry | null>>;
}

/**
 * Custom hook for managing modal state across the application
 * Extracted from ProductMatrixNew.tsx to reduce component complexity
 *
 * Consolidates 8 useState hooks and their handlers into a single hook
 */
export const useModalState = (): ModalState => {
  // Device Modal State
  const [selectedDevice, setSelectedDevice] = useState<Product | null>(null);
  const [showDeviceModal, setShowDeviceModal] = useState<boolean>(false);

  // Partner Modal State
  const [selectedPartnerProduct, setSelectedPartnerProduct] = useState<Product | null>(null);
  const [showPartnerModal, setShowPartnerModal] = useState<boolean>(false);

  // Product Modal State
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showProductModal, setShowProductModal] = useState<boolean>(false);

  // Onboarding Modal State
  const [selectedOnboardingCountry, setSelectedOnboardingCountry] =
    useState<OnboardingCountry | null>(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(false);

  // Device Modal Handlers
  const handleDeviceClick = useCallback((device: Product) => {
    setSelectedDevice(device);
    setShowDeviceModal(true);
  }, []);

  const handleCloseDeviceModal = useCallback(() => {
    setShowDeviceModal(false);
    setSelectedDevice(null);
  }, []);

  // Partner Modal Handlers
  const handlePartnerClick = useCallback((product: Product) => {
    setSelectedPartnerProduct(product);
    setShowPartnerModal(true);
  }, []);

  const handleClosePartnerModal = useCallback(() => {
    setShowPartnerModal(false);
    setSelectedPartnerProduct(null);
  }, []);

  // Product Modal Handlers
  const handleProductClick = useCallback((product: Product) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  }, []);

  const handleCloseProductModal = useCallback(() => {
    setShowProductModal(false);
    setSelectedProduct(null);
  }, []);

  // Onboarding Modal Handlers
  const handleOnboardingClick = useCallback((data: OnboardingCountry) => {
    setSelectedOnboardingCountry(data);
    setShowOnboardingModal(true);
  }, []);

  const handleCloseOnboardingModal = useCallback(() => {
    setShowOnboardingModal(false);
    setSelectedOnboardingCountry(null);
  }, []);

  return {
    // Device Modal
    selectedDevice,
    showDeviceModal,
    handleDeviceClick,
    handleCloseDeviceModal,

    // Partner Modal
    selectedPartnerProduct,
    showPartnerModal,
    handlePartnerClick,
    handleClosePartnerModal,

    // Product Modal
    selectedProduct,
    showProductModal,
    handleProductClick,
    handleCloseProductModal,

    // Onboarding Modal
    selectedOnboardingCountry,
    showOnboardingModal,
    handleOnboardingClick,
    handleCloseOnboardingModal,
    setSelectedOnboardingCountry, // Exposed for routing logic
  };
};
